import { useFieldApi } from '@data-driven-forms/react-form-renderer';
import { forwardRef, useEffect } from 'react';

import FormControl from '~/common/componentMapper/FormControl';
import FormErrorMessage from '~/common/componentMapper/FormErrorMessage';
import ChakraCheckbox from '~/common/components/Checkboxes/Checkbox/Checkbox';
import GenericTooltip from '~/common/components/GenericTooltip';

import { validationError } from '../utils';

type CheckboxProps = {
  name: string;
  input: any;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  value?: any;
  meta?: any;
  validateOnMount?: boolean;
  isInvalid?: boolean;
  sx?: any;
  checkboxSx?: any;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const Checkbox = forwardRef<any, CheckboxProps>((props, ref) => {
  const {
    input,
    label,
    helperText,
    required,
    isDisabled,
    readOnly,
    value,
    meta,
    validateOnMount,
    isInvalid,
    sx,
    checkboxSx,
    showCheckedWhenDisabled = false,
    ...rest
  } = useFieldApi({
    ...props,
    type: 'checkbox',
  });
  const { name } = props;

  const invalid = validationError(meta, validateOnMount);
  const errorText =
    invalid ||
    ((meta.touched || validateOnMount) && meta.warning) ||
    helperText ||
    isInvalid ||
    meta.error;
  const showError = !(errorText === true || errorText === false);

  useEffect(() => {
    if (input?.checked) {
      input.onChange(`${input.checked}`);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    if (!isDisabled && !readOnly) {
      input.onChange(event);
    }
  };

  return (
    <FormControl
      isInvalid={!!invalid && !isDisabled && showError}
      isRequired={required}
      isDisabled={isDisabled || readOnly}
      error={!!invalid}
      sx={sx}
      id={name}
      {...rest}
    >
      <ChakraCheckbox
        value={value}
        {...input}
        isChecked={
          input.checked && (!isDisabled || showCheckedWhenDisabled) && !readOnly
        }
        sx={checkboxSx}
        ref={ref}
        onChange={handleChange}
      >
        {label}
        {helperText && <GenericTooltip label={helperText} />}
      </ChakraCheckbox>

      {showError && <FormErrorMessage>{errorText}</FormErrorMessage>}
    </FormControl>
  );
});

Checkbox.displayName = 'CheckboxWithRef';

export default Checkbox;
